import type { Pattern } from '../types/state';
import type { Session } from '../types/session';
import type { IRState } from './engine.ir';
import { generateDsl } from './dsl-generator';

/**
 * Deep-clones an IR node tree, regenerating every node's `id` via
 * crypto.randomUUID() except for IDs in `preserve`, which are kept as-is.
 *
 * Use this when forking a pattern under a live session: the active path's
 * IDs (and any nodes referenced by EXPAND_NODE events) must survive so that
 * history replay continues to resolve correctly against the fork.
 */
export function cloneIRNodePreservingIds<T>(node: T, preserve: Set<string>): T {
  if (node === null || typeof node !== 'object') return node;
  if (Array.isArray(node)) {
    return node.map((item) => cloneIRNodePreservingIds(item, preserve)) as unknown as T;
  }
  const cloned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    if (key === 'id' && typeof value === 'string') {
      cloned[key] = preserve.has(value) ? value : crypto.randomUUID();
    } else {
      cloned[key] = cloneIRNodePreservingIds(value, preserve);
    }
  }
  return cloned as T;
}

/**
 * Collects every node ID reachable from `pattern` that should be preserved
 * across a fork: the active path (so the engine can keep replaying the
 * session) plus any node referenced by an EXPAND_NODE event in `history`.
 */
export function idsToPreserveForFork(
  activePathIds: string[],
  history: { action: string; nodeId?: string }[],
): Set<string> {
  const out = new Set<string>(activePathIds);
  for (const e of history) {
    if (e.action === 'EXPAND_NODE' && e.nodeId) out.add(e.nodeId);
  }
  return out;
}

/**
 * Returns `desired` when it doesn't appear in `existing`; otherwise appends
 * a numeric suffix until unique. Falls back to a short UUID suffix after 999
 * collisions to avoid an infinite loop on pathological libraries.
 */
export function makeUniqueTitle(desired: string, existing: Set<string>): string {
  if (!existing.has(desired)) return desired;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${desired} (${n})`;
    if (!existing.has(candidate)) return candidate;
  }
  return `${desired} (${crypto.randomUUID().slice(0, 6)})`;
}

interface ForkForSessionInput {
  original: Pattern;
  session: Session;
  irState: IRState;
  desiredName: string;
  existingTitles: Set<string>;
}

/**
 * Builds a forked Pattern derived from `original`, suitable for saving via
 * persistence.savePattern. The fork's root structure is a deep clone with
 * fresh node IDs except for those on the active path or referenced by
 * EXPAND_NODE events still in history — those IDs are preserved so the
 * session's history continues to replay correctly against the fork.
 */
export function forkForSession(input: ForkForSessionInput): Pattern {
  if (!input.original.structure) {
    throw new Error('forkForSession: original pattern has no structure');
  }

  const activePathIds = input.irState.path.map((p) => p.id);
  if (input.irState.activeNodeId) activePathIds.push(input.irState.activeNodeId);
  const preserve = idsToPreserveForFork(activePathIds, input.session.history);
  const structure = cloneIRNodePreservingIds(input.original.structure, preserve);
  const title = makeUniqueTitle(input.desiredName, input.existingTitles);
  structure.name = title;

  return {
    id: crypto.randomUUID(),
    parentPatternId: input.original.id,
    title,
    structure,
    dsl: generateDsl(structure),
  };
}
